import type { BranchAccess, BranchPermission, ModuleKey } from '../store/useSessionStore';
import {
  isMappedPermissionAction,
  isRestrictivePermissionAction,
} from '../permissions/permissionCatalog';

function actionsHasKey(actions: Record<string, boolean> | undefined, action: string): boolean {
  return !!actions && Object.prototype.hasOwnProperty.call(actions, action);
}

/** View-ish custom actions inherit canView when key absent; else inherit canEdit. */
function inheritsFromView(action: string): boolean {
  return (
    action.startsWith('view_') ||
    action === 'export' ||
    action === 'sales' ||
    action === 'cash' ||
    action === 'expenses' ||
    action === 'inventory' ||
    action === 'fiado' ||
    action === 'margins' ||
    action === 'by_employee' ||
    action === 'appointments' ||
    action === 'sales_view' ||
    action === 'history'
  );
}

/**
 * Resolve whether a permission row allows `action`.
 * Same mapping as SQL `public.assert_module_permission`.
 *
 * Custom actions: explicit true/false in `actions` wins; if key absent, inherit
 * canView (view_*, export, report families) or canEdit (mutations) so Equipo can
 * tighten later without locking owners / current canEdit employees out.
 * Restrictive keys never inherit — require explicit true (owner bypasses via bootstrap).
 */
export function permissionAllowsAction(
  permission: BranchPermission | undefined,
  action: string
): boolean {
  if (!permission) return false;
  const key = action.trim().toLowerCase();
  if (!key) return false;

  if (key === 'view') return permission.canView === true;
  if (key === 'edit' || key === 'operate' || key === 'sell' || key === 'open' || key === 'close' || key === 'create') {
    return permission.canEdit === true;
  }
  if (key === 'void') return permission.canVoid === true;
  if (key === 'discount') return permission.canDiscount === true;
  if (key === 'manage_agenda') return permission.canManageAgenda === true;
  if (key === 'view_others') return permission.canViewOthers === true;

  if (isMappedPermissionAction(key)) {
    return false;
  }

  if (actionsHasKey(permission.actions, key)) {
    return permission.actions![key] === true;
  }

  if (isRestrictivePermissionAction(key)) {
    return false;
  }

  // ponytail: inherit until Equipo personalizes the key
  return inheritsFromView(key) ? permission.canView === true : permission.canEdit === true;
}

export function canModuleAction(
  branches: BranchAccess[],
  branchId: string | undefined,
  module: ModuleKey,
  action: string
): boolean {
  if (!branchId) return false;
  const branch = branches.find((item) => item.id === branchId);
  if (!branch) return false;
  // Offline unknown permissions: fail-closed for mutations / actions.
  if (branch.permissionsOfflineUnknown) return false;
  const permission = branch.permissions.find((item) => item.module === module);
  return permissionAllowsAction(permission, action);
}
